import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';
import { testIdAttr, testIdPart } from '@/lib/testIds';

import { matchDestinations, searchIndex, type Destination } from '../search';
import { useSearchSources, type SearchOption } from './useSearchSources';
import type { Theme } from '@mui/material/styles';

/** A single letter would list half the portal. */
const MIN_LENGTH = 2;

function toOption(destination: Destination): SearchOption {
  return {
    key: `${destination.group}:${destination.path}:${destination.label}`,
    label: destination.label,
    group: destination.group,
    // Under a sub-page, the section it lives in; under a section, what it does.
    secondary: destination.section ?? destination.hint,
    path: destination.path,
    // A section and its first page can share an address (Notifications and
    // All Notifications), so a page inside a section is told apart.
    testId: destination.section
      ? `global-search-${testIdPart(destination.path)}-subpage-option`
      : `global-search-${testIdPart(destination.path)}-option`,
  };
}

/**
 * The top bar's search: pages by name, and for staff, organizations too.
 *
 * Below 600px it is a "Search" button that opens the same search full width,
 * so the avatar menu - the only way to Log Out - still fits beside it from
 * 320px (arc-and-discovery-hardening, B29, D18).
 */
export function GlobalSearch() {
  const phone = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [opened, setOpened] = useState(false);

  if (!phone) return <SearchField />;

  return (
    <>
      <IconButton
        aria-label="Search"
        onClick={() => setOpened(true)}
        data-testid="global-search-open-button"
      >
        <SearchIcon />
      </IconButton>
      <Dialog
        open={opened}
        onClose={() => setOpened(false)}
        fullWidth
        // At the top, where the box would be, with room below for the list.
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        slotProps={{
          paper: { 'aria-label': 'Search', ...testIdAttr('global-search-dialog') },
        }}
      >
        <Box sx={{ p: 2 }}>
          <SearchField fullWidth focusOnOpen onChosen={() => setOpened(false)} />
        </Box>
      </Dialog>
    </>
  );
}

/**
 * The search box and its results.
 *
 * MUI's Autocomplete is the combobox pattern - Down opens and moves through
 * the results with the active one announced, Enter chooses, Escape closes -
 * so nothing here handles a key itself. The list only ever holds matches, so
 * the component's own filtering is switched off.
 */
function SearchField({
  fullWidth = false,
  focusOnOpen = false,
  onChosen,
}: {
  fullWidth?: boolean;
  /** For the phone dialog: the person just asked to search, so the box takes the focus. */
  focusOnOpen?: boolean;
  onChosen?: () => void;
}) {
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focusOnOpen) box.current?.focus();
  }, [focusOnOpen]);

  const { user } = useAuth();
  const { viewing } = useViewingAs();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const searchId = useId();

  const index = useMemo(
    () => searchIndex(user?.role ?? null, Boolean(viewing)),
    [user?.role, viewing],
  );
  const sources = useSearchSources(inputValue);
  const enough = inputValue.trim().length >= MIN_LENGTH;
  const options = enough
    ? [...matchDestinations(index, inputValue).map(toOption), ...sources]
    : [];

  return (
    <Autocomplete<SearchOption>
      options={options}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        setInputValue(value);
        if (reason === 'input') setOpen(true);
      }}
      onChange={(_, option) => {
        if (!option?.path) return;
        setInputValue('');
        setOpen(false);
        onChosen?.();
        void navigate(option.path);
      }}
      open={open && enough}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      filterOptions={(shown) => shown}
      groupBy={(option) => option.group}
      // Each group a labelled group of options. MUI's own markup put the
      // options in a plain list inside a list item, which a screen reader
      // cannot follow as one listbox - the accessibility scan failed it.
      renderGroup={(params) => {
        const labelId = `${searchId}-group-${String(params.key)}`;
        return (
          <Box
            component="li"
            key={params.key}
            role="group"
            aria-labelledby={labelId}
            sx={{ listStyle: 'none' }}
          >
            <ListSubheader id={labelId} component="div" role="presentation">
              {params.group}
            </ListSubheader>
            <Box component="ul" role="presentation" sx={{ p: 0, m: 0 }}>
              {params.children}
            </Box>
          </Box>
        );
      }}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.key}
      getOptionDisabled={(option) => Boolean(option.disabled)}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      noOptionsText="No pages match"
      // The first match is the active one, so a few letters and Enter is enough.
      autoHighlight
      openOnFocus={false}
      clearOnBlur
      popupIcon={null}
      forcePopupIcon={false}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={key} {...rest} data-testid={option.testId}>
            <Box sx={{ minWidth: 0 }}>
              <Typography>{option.label}</Typography>
              {option.secondary ? (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {option.secondary}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search"
          size="small"
          inputRef={box}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              'aria-label': 'Search',
              ...testIdAttr('global-search-input'),
            },
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
      slotProps={{
        listbox: testIdAttr('global-search-list'),
        popper: {
          // Opens leftwards from the box's right edge, so on a phone - where
          // the box is narrow and near the right - the list stays on screen.
          placement: 'bottom-end',
          sx: { width: 'min(360px, calc(100vw - 32px)) !important' },
        },
      }}
      sx={{ width: fullWidth ? '100%' : 240 }}
    />
  );
}
